/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */
import {
  addDays,
  addMinutes,
  format,
  formatISO,
  isSameDay,
  parseISO,
  setHours,
  setMinutes,
  startOfDay
} from 'date-fns';
import * as React from 'react';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';

import { FormContext } from '@/helper/context';

import { CustomInput } from './datepicker-custom';

interface Properties {
  value?: Times;
  valueSelect?: Times;
  pastMidnightMixed?: boolean;
  bulkLatestStartTime?: Date;
}
interface Times {
  startDate?: string;
  startTime?: string;
  endTime?: string;
  pastMidnight?: boolean;
}

function parseDateValue(value?: string) {
  if (!value) return;
  const parsed = parseISO(value);
  if (Number.isNaN(parsed.getTime())) return;
  return startOfDay(parsed);
}

function parseTimeValue(value?: string) {
  if (!value) return;
  if (value.includes('T')) {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return;
  const base = new Date();
  base.setHours(hours, minutes, 0, 0);
  return base;
}

function formatDate(value?: string) {
  const parsed = parseDateValue(value);
  return parsed ? format(parsed, 'yyyy-MM-dd') : '';
}

function formatTime(value?: string) {
  const parsed = parseTimeValue(value);
  return parsed ? format(parsed, 'HH:mm') : '';
}

function buildDateTime(date: Date, time: Date) {
  const updated = new Date(date);
  updated.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return updated;
}

export function SelectorDateTime({ value, valueSelect, pastMidnightMixed = false, bulkLatestStartTime }: Properties) {
  const { changeTracker, setWasChanged, disabled, errors } = React.useContext(FormContext);

  const baseStartDate = formatDate(value?.startDate ?? value?.startTime);
  const baseStartTime = formatTime(value?.startTime);
  const baseEndTime = formatTime(value?.endTime);
  const basePastMidnight = pastMidnightMixed
    ? undefined
    : value?.pastMidnight ??
      (value?.startTime && value?.endTime ? !isSameDay(parseISO(value.startTime), parseISO(value.endTime)) : false);

  const selectedStartDate = formatDate(valueSelect?.startDate ?? valueSelect?.startTime);
  const selectedStartTime = formatTime(valueSelect?.startTime);
  const selectedEndTime = formatTime(valueSelect?.endTime);
  const selectedPastMidnight =
    valueSelect?.pastMidnight ??
    (valueSelect?.startTime && valueSelect?.endTime
      ? !isSameDay(parseISO(valueSelect.startTime), parseISO(valueSelect.endTime))
      : undefined);

  const [startDateState, setStartDateState] = React.useState<Date | undefined>(
    parseDateValue(selectedStartDate || baseStartDate)
  );
  const [startTimeState, setStartTimeState] = React.useState<Date | undefined>(
    parseTimeValue(selectedStartTime || baseStartTime)
  );
  const [endTimeState, setEndTimeState] = React.useState<Date | undefined>(
    parseTimeValue(selectedEndTime || baseEndTime)
  );
  const [pastMidnight, setPastMidnight] = React.useState<boolean>(selectedPastMidnight ?? basePastMidnight ?? false);
  const [pastMidnightActive, setPastMidnightActive] = React.useState(!pastMidnightMixed);

  React.useEffect(() => {
    const didChangeStartDate = selectedStartDate ? selectedStartDate !== baseStartDate : false;
    const didChangeStartTime = selectedStartTime ? selectedStartTime !== baseStartTime : false;
    const didChangeEndTime = selectedEndTime ? selectedEndTime !== baseEndTime : false;
    const didChangePastMidnight =
      selectedPastMidnight === undefined ? false : selectedPastMidnight !== basePastMidnight;
    changeTracker.current = {
      ...changeTracker.current,
      startDate: didChangeStartDate,
      startTime: didChangeStartTime,
      endTime: didChangeEndTime,
      pastMidnight: didChangePastMidnight
    };
    setWasChanged(Object.values(changeTracker.current).includes(true));
  }, []);

  React.useEffect(() => {
    setPastMidnightActive(!pastMidnightMixed);
  }, [pastMidnightMixed]);

  const maxTime = setHours(setMinutes(new Date(), 45), 23);
  const minTime = pastMidnight
    ? startOfDay(new Date())
    : startTimeState
    ? addMinutes(startTimeState, 15)
    : bulkLatestStartTime
    ? addMinutes(bulkLatestStartTime, 15)
    : startOfDay(new Date());

  function updateChangeTracker(field: string, changed: boolean) {
    changeTracker.current = {
      ...changeTracker.current,
      [field]: changed
    };
    setWasChanged(Object.values(changeTracker.current).includes(true));
  }

  function setStartDate(date: Date) {
    if (!date) return;
    setStartDateState(date);
    updateChangeTracker('startDate', format(date, 'yyyy-MM-dd') !== baseStartDate);
  }

  function setStartTime(date: Date) {
    if (!date) return;
    const updatedStartTime = date;
    setStartTimeState(updatedStartTime);
    updateChangeTracker('startTime', format(date, 'HH:mm') !== baseStartTime);

    if (!pastMidnight && endTimeState) {
      const start = buildDateTime(startOfDay(new Date()), updatedStartTime);
      const end = buildDateTime(startOfDay(new Date()), endTimeState);
      if (end.getTime() <= start.getTime()) {
        const nextEnd = addMinutes(updatedStartTime, 15);
        setEndTimeState(nextEnd);
        updateChangeTracker('endTime', format(nextEnd, 'HH:mm') !== baseEndTime);
      }
    }
  }

  function setEndTime(date: Date) {
    if (!date) return;
    setEndTimeState(date);
    updateChangeTracker('endTime', format(date, 'HH:mm') !== baseEndTime);
  }

  function togglePastMidnight(checked: boolean) {
    setPastMidnight(checked);
    const startBaseline = startTimeState ?? bulkLatestStartTime;
    if (!checked && startBaseline) {
      const start = buildDateTime(startOfDay(new Date()), startBaseline);
      const end = endTimeState ? buildDateTime(startOfDay(new Date()), endTimeState) : undefined;
      if (!end || end.getTime() <= start.getTime()) {
        const nextEnd = addMinutes(startBaseline, 15);
        setEndTimeState(nextEnd);
        updateChangeTracker('endTime', format(nextEnd, 'HH:mm') !== baseEndTime);
      }
    }
    updateChangeTracker('pastMidnight', checked !== basePastMidnight);
  }
  const isPastMidnightMixed = pastMidnightMixed && !pastMidnightActive;

  const canTogglePastMidnight = !disabled && Boolean(startTimeState);

  function handlePastMidnightMixedActivate() {
    if (!isPastMidnightMixed || !canTogglePastMidnight) return;
    setPastMidnightActive(true);
    togglePastMidnight(!pastMidnight);
  }

  const startDateDisabled = disabled;
  const startTimeDisabled = disabled;
  const hasStartBaseline = Boolean(startTimeState || bulkLatestStartTime);
  const endTimeDisabled = disabled || !hasStartBaseline;
  const pastMidnightDisabled = disabled || !hasStartBaseline || isPastMidnightMixed;

  const baseDate = startDateState ?? startOfDay(new Date());
  const startTimeValue = startTimeState ? buildDateTime(baseDate, startTimeState) : undefined;
  const endDate = addDays(baseDate, pastMidnight ? 1 : 0);
  const endTimeValue = endTimeState ? buildDateTime(endDate, endTimeState) : undefined;

  return (
    <React.Fragment>
      <Form.Control type='hidden' name='startDate' value={startDateState ? formatISO(startDateState) : ''} />
      <Form.Control type='hidden' name='startTime' value={startTimeValue ? startTimeValue.toISOString() : ''} />
      <Form.Control type='hidden' name='endTime' value={endTimeValue ? endTimeValue.toISOString() : ''} />

      <Form.Group className='mt-3 col-6'>
        <span className={'d-block form-label' + (errors?.startDate ? ' is-invalid' : '')}>Start Date</span>
        <DatePicker
          selected={startDateState}
          disabled={startDateDisabled}
          onChange={(date) => setStartDate(date)}
          dateFormat='MMM do, yyyy'
          customInput={<CustomInput id='startDateSelector' disabled={startDateDisabled} />}
        />
        <Form.Control.Feedback type='invalid'>{errors.startDate}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mt-3 col-3'>
        <span className={'d-block form-label' + (errors?.startTime ? ' is-invalid' : '')}>Start Time</span>
        <DatePicker
          selected={startTimeState}
          disabled={startTimeDisabled}
          onChange={(date) => setStartTime(date)}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          dateFormat='h:mm aa'
          customInput={<CustomInput id='startTimeSelector' disabled={startTimeDisabled} />}
        />
        <Form.Control.Feedback type='invalid'>{errors.startTime}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mt-3 col-3'>
        <span className={'d-block form-label' + (errors?.endTime ? ' is-invalid' : '')}>End Time</span>
        <DatePicker
          selected={endTimeState}
          disabled={endTimeDisabled}
          onChange={(date) => setEndTime(date)}
          minTime={minTime}
          maxTime={maxTime}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          dateFormat='h:mm aa'
          customInput={<CustomInput id='endTimeSelector' disabled={endTimeDisabled} />}
        />
        <Form.Control.Feedback type='invalid'>{errors.endTime}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mt-3 col-12 offset-6' onClick={handlePastMidnightMixedActivate}>
        <Form.Check
          type='checkbox'
          id='pastMidnight'
          name='pastMidnight'
          label='Past Midnight'
          checked={pastMidnight}
          onChange={(event) => togglePastMidnight(event.target.checked)}
          disabled={pastMidnightDisabled}
          className='small text-muted'
        />
      </Form.Group>
    </React.Fragment>
  );
}
